import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    Addon,
    AddonsContainer,
    learnMoreEventData
} from "@/components/wpl/pricing/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { AtlassianGuardMarkBrand } from "@/libraries/wpl/foundations/src/logos/atlassian-guard-mark-brand";
import { JiraProductDiscoveryMarkBrand } from "@/libraries/wpl/foundations/src/logos/jiraproductdiscovery-mark-brand";

const logoContainerCss = css({
    height: 60
});

const jpdLogoCss = css({
    position: "relative",
    insetBlockStart: -16,
    insetInlineStart: -12
});

export const JiraAddons = () => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    return (
        <AddonsContainer
            heading={
                <FormattedMessage
                    id="JiraSoftwarePricingPageTemplate.addons.sectionHeader"
                    defaultMessage="Power up your team on Jira"
                />
            }
        >
            <Addon
                logo={
                    <div css={logoContainerCss}>
                        <AtlassianGuardMarkBrand
                            style={{
                                height: 60,
                                width: 60
                            }}
                        />
                    </div>
                }
                heading={intl.formatMessage(
                    {
                        id: "JiraSoftwarePricingPageTemplate.addons.atlassianGuard.heading",
                        defaultMessage:
                            "Enhance cloud security with <b>Atlassian Guard</b>"
                    },
                    {
                        b: (chunks: ReactNode) => <b>{chunks}</b>
                    }
                )}
                subheading={intl.formatMessage({
                    id: "JiraSoftwarePricingPageTemplate.addons.atlassianGuard.subheading",
                    defaultMessage: "Priced separately, per unique user"
                })}
                features={[
                    intl.formatMessage({
                        id: "JiraSoftwarePricingPageTemplate.addons.atlassianGuard.features.auth",
                        defaultMessage:
                            "Enforce SSO, 2FA, and mobile policies on managed and external users"
                    }),
                    intl.formatMessage({
                        id: "JiraSoftwarePricingPageTemplate.addons.atlassianGuard.features.classificationAndDetection",
                        defaultMessage:
                            "Set classification levels and detect suspicious activity"
                    }),
                    intl.formatMessage({
                        id: "JiraSoftwarePricingPageTemplate.addons.atlassianGuard.features.remediation",
                        defaultMessage:
                            "Respond with actionable remediation steps"
                    })
                ]}
                learnMore={{
                    text: intl.formatMessage({
                        id: "JiraSoftwarePricingPageTemplate.addons.atlassianGuard.learnMore",
                        defaultMessage: "Learn more"
                    }),
                    href: useLocalizedHref("/software/access"),
                    onClick: () =>
                        createAnalyticsEvent({
                            ...learnMoreEventData,
                            attributes: {
                                product: "jira",
                                addon: "atlassianGuard"
                            }
                        }).fire()
                }}
            />

            <Addon
                logo={
                    <div css={logoContainerCss}>
                        <JiraProductDiscoveryMarkBrand
                            css={jpdLogoCss}
                            style={{
                                height: 88,
                                width: 88
                            }}
                        />
                    </div>
                }
                heading={intl.formatMessage(
                    {
                        id: "JiraSoftwarePricingPageTemplate.addons.jpd.heading",
                        defaultMessage:
                            "Prioritize and roadmap with <b>Jira Product Discovery</b>"
                    },
                    {
                        b: (chunks: ReactNode) => <b>{chunks}</b>
                    }
                )}
                subheading={intl.formatMessage({
                    id: "JiraSoftwarePricingPageTemplate.addons.jpd.subheading",
                    defaultMessage: "$10 per creator / per month"
                })}
                features={[
                    intl.formatMessage({
                        id: "JiraSoftwarePricingPageTemplate.addons.jpd.features.ideasAndInsights",
                        defaultMessage:
                            "Capture and prioritize ideas and insights"
                    }),
                    intl.formatMessage({
                        id: "JiraSoftwarePricingPageTemplate.addons.jpd.features.alignment",
                        defaultMessage:
                            "Align everyone with custom, shareable roadmaps"
                    }),
                    intl.formatMessage({
                        id: "JiraSoftwarePricingPageTemplate.addons.jpd.features.jiraIntegration",
                        defaultMessage:
                            "Seamlessly integrate with Jira for delivery"
                    })
                ]}
                learnMore={{
                    text: intl.formatMessage({
                        id: "JiraSoftwarePricingPageTemplate.addons.jpd.learnMore",
                        defaultMessage: "Learn more"
                    }),
                    href: useLocalizedHref("/software/jira/product-discovery"),
                    onClick: () =>
                        createAnalyticsEvent({
                            ...learnMoreEventData,
                            attributes: {
                                product: "jira",
                                addon: "jpd"
                            }
                        }).fire()
                }}
            />
        </AddonsContainer>
    );
};
